import { ClassRoomCalendarCardItemDTO } from "./ClassRoomCalendarCardItemDTO.tsx";
import { useTranslation } from "react-i18next";
import { CalendarCardComponent } from "../CalendarCardComponent.tsx";
import { useLeaveClassRoom } from "./useLeaveClassRoom.ts";
import { ErrorMessagePopup } from "../../UI/MessagePopup/ErrorMessagePopup.tsx";
import { useLeavingClassErrorHandler } from "./UseLeavingClassErrorHandler.tsx";
import { ExtendedClassRoomType } from "../CalendarCardComponentProps.tsx";
import { useMyRemaining1on1Lessons } from "../../User/Plan/useMyRemaining1on1Lessons.ts";

type Props = {
  classRoom: ClassRoomCalendarCardItemDTO;
  showModal: boolean;
  onClose: () => void;
  onOpen: () => void;
};

export function SpeakingLessonCalendarCard({
  classRoom,
  showModal,
  onOpen,
  onClose,
}: Props) {
  const { t } = useTranslation("speakingClassCard");

  const { show, title, description, handleError, closePopup } =
    useLeavingClassErrorHandler();
  const { leaveClassRoom, loading } = useLeaveClassRoom();
  const { cancellationsUsed, maxCancellations } = useMyRemaining1on1Lessons();

  const has1on1CancellationLimitReached =
    classRoom.is1on1 &&
    maxCancellations !== null &&
    cancellationsUsed >= maxCancellations;

  async function leave() {
    try {
      await leaveClassRoom(classRoom.id);
    } catch (e) {
      handleError(e as Error);
      throw e;
    }
  }

  const cardTitle = classRoom.is1on1
    ? t("oneOnOneClassTitle")
    : classRoom.topic.name;
  const cardPdfUrl = classRoom.is1on1 ? undefined : classRoom.topic.pdfUrl;

  return (
    <>
      <ErrorMessagePopup
        onClose={closePopup}
        title={title}
        description={description}
        show={show}
      />
      <CalendarCardComponent
        showModal={showModal}
        onOpen={onOpen}
        onClose={onClose}
        showLessonTopicBadge
        title={cardTitle}
        startDateUtc={classRoom.startedAtUtc}
        level={classRoom.speakingLevel}
        participantsCount={classRoom.participantsCount}
        capacity={classRoom.capacity}
        type={
          classRoom.is1on1 ? ExtendedClassRoomType.OneOnOne : classRoom.type
        }
        ctaLabel={t("cancelSpeakingLesson")}
        ctaAction={leave}
        pdfUrl={cardPdfUrl}
        meetUri={classRoom.meetingUri}
        loadingCta={loading}
        disabled={!classRoom.canBeLeaved}
        disabledReason={t("cancelSpeakingLessonDisabledDueToTime")}
        hideCta={has1on1CancellationLimitReached}
        slotTimezone={classRoom.timezone}
      />
    </>
  );
}
